import { rgba } from 'polished';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

const Container = styled(Flex)`
	border-radius: 10px;
	position: relative;
	background-color: ${(props) => props.theme.colors.lightBlueLight};
	align-items: center;
`;

const Segment = styled.div<{ selected?: boolean }>`
	color: ${(props) => props.theme.colors.slateBlue};
	background-color: transparent;
	padding: 8px 20px;
	position: relative;
	text-align: center;
	cursor: pointer;

	:hover {
		color: ${(props) => props.theme.colors.antracite};
	}

	&:not(:last-child) {
		margin-right: 4px;
	}

	${(props) =>
		props.selected &&
		`
		color: ${props.theme.colors.antracite};
	`}
`;

const SelectedSegmentBackground = styled(Box)`
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)};
	height: calc(100% - 10px);
	position: absolute;
	background-color: ${(props) => props.theme.colors.white};
	transition: 300ms cubic-bezier(0, 1, 0, 1) left;
	border-radius: 6px;
`;

export function SegmentedControl(props: {
	value: string;
	options: { title: string; value: string }[];
	onChange: (value: string) => void;
}) {
	const segmentRefs = useMemo(
		() =>
			Array(props.options.length)
				.fill(0)
				.map(() => createRef<HTMLDivElement>()),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const parentRef = useRef<HTMLDivElement | null>(null);
	const [selectedBounds, setSelectedBounds] = useState({ left: 0, width: 100 });

	useEffect(() => {
		if (segmentRefs.length !== props.options.length) {
			return;
		}

		const selectedIndex = props.options.findIndex(({ value }) => value === props.value);
		const bounds = segmentRefs[selectedIndex].current!.getBoundingClientRect();
		const parentBounds = parentRef.current!.getBoundingClientRect();

		setSelectedBounds({
			left: bounds.left - parentBounds.left,
			width: bounds.width,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segmentRefs.length]);

	return (
		<Container p="5px" ref={parentRef}>
			<SelectedSegmentBackground style={selectedBounds} />
			{props.options.map((option, index) => (
				<Segment
					key={index}
					onClick={(element) => {
						const bounds = element.currentTarget.getBoundingClientRect();
						const parentBounds = parentRef.current!.getBoundingClientRect();
						setSelectedBounds({ left: bounds.left - parentBounds.left, width: bounds.width });

						props.onChange(option.value);
					}}
					selected={option.value === props.value}
					ref={segmentRefs[index]}
				>
					{option.title}
				</Segment>
			))}
		</Container>
	);
}
